import {AfterViewInit, Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import moment from 'moment';
import {BehaviorSubject, Observable, share, Subscription} from 'rxjs';
import {first, map} from 'rxjs/operators';
import {v4 as uuidv4} from 'uuid';
import {LocalStorageService} from '../local-storage.service';
import {DatumFormatPipe} from '../pipes/datum-format.pipe';
import {AuftragDialogComponent} from './dialogs/auftrag-dialog/auftrag-dialog.component';
import {KostenstelleDialogComponent} from './dialogs/kostenstelle-dialog/kostenstelle-dialog.component';
import {SpesenDialogComponent} from './dialogs/spesen-dialog/spesen-dialog.component';
import {Employees, EmpWeekPlan, EmpWorkPlan} from './models/employees-model';
import {IndexedDbData} from './models/indexedDB-model';
import {OrderListElement, Orders} from './models/order-model';
import {CostCenter, EmptyCostCenter, EmptyOrder, EmptyOrderData, OrderData, TimeControlEntry} from './models/timeControlEntry';
import {CaptureImageService} from './services/capture-image.service';
import {DataService} from './services/data.service';
import {DialogService} from './services/dialog.service';
import {SharedDataService} from './services/shared-data.service';
import {VinavonUpdateService} from '../services/vinavon-update.service';
import {MessageBoxService} from '../services/message-box.service';
import {UserAuthenticationService} from '../services/user-authentication.service';
import {HelperService} from '../services/helper.service';
import {DeviceDetectionService} from '../services/device-detection-service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {BenutzerModels} from '../models/benutzer-models';
import UserLicenses = BenutzerModels.UserLicenses;

@Component({
    selector: 'app-time-app', templateUrl: './time-app.component.html', styleUrls: ['./time-app.component.scss'],
})
export class TimeAppComponent implements OnInit, AfterViewInit, OnDestroy {
    public toggleIcon = false;
    public IndexedDbData!: IndexedDbData;
    public data$: BehaviorSubject<OrderData> = new BehaviorSubject(EmptyOrderData());
    public toggleBtn = false;
    public userPermissions!: Employees;
    public currentIndexDBSubscriber$!: Subscription;
    public currentMessageSubscriber$!: Subscription;
    public startEmptyWorkTime = false;
    public sharedMessage!: string;
    protected readonly hasTimeAppPlusAccess$: Observable<boolean>;

    constructor(private route: ActivatedRoute,
                public deviceDetection: DeviceDetectionService,
                public helperService: HelperService,
                private dialogService: DialogService,
                private dataService: DataService,
                private localStorage: LocalStorageService,
                private shareDataService: SharedDataService,
                private captureImageService: CaptureImageService,
                private userAuthService: UserAuthenticationService,
                private router: Router,
                private _datumFormatPipe: DatumFormatPipe,
                public svcVinavonUpdate: VinavonUpdateService,
                private messageBoxService: MessageBoxService) {
        this.helperService.setPageRefreshURL('/timeapp');
        if (this.dataService.dbDataHasLoaded) {
            this.dataServiceDataLoaded();
        } else {
            setTimeout(async () => {
                await this.helperService.setPageRefreshURL('/timeapp');
                await this.dataServiceDataLoaded();
            }, 500);
        }

        this.hasTimeAppPlusAccess$ = this.userAuthService.getCurrentUser().pipe(
            takeUntilDestroyed(),
            map((user) => {
                if (!user) {
                    return false;
                }
                return user.allowRecordingMaterial;
            }),
            share({connector: () => new BehaviorSubject(false)}));
    }

    ngOnInit(): void {

    }

    ngAfterViewInit(): void {
    }

    ngOnDestroy(): void {
        this.shareDataService.changeMessage(null, '');
        this.shareDataService.scannedData('');
        if (this.currentIndexDBSubscriber$) {
            this.currentIndexDBSubscriber$.unsubscribe();
        }
    }

    async getDataFromLocalStorage(): Promise<void> {
        const indexedDbData = await this.localStorage.getAllDataFromIndexedDB();
        if (indexedDbData.allEmployees) {
            this.IndexedDbData = indexedDbData;
            await this.updateUserPermissions();
            await this.loadTimeControlEntries();
        }
    }

    afterInit(): void {
        if (this.shareDataService.called) {
            this.currentMessageSubscriber$ = this.shareDataService.currentMessage.pipe(first()).subscribe({
                next: async (res) => {
                    this.sharedMessage = res.action;
                    switch (res.action) {
                        case 'start':
                            this.starOrderTime(res.order);
                            break;
                        case 'copy':
                            this.copyOrder(res.order);
                            break;
                        case 'edit':
                            this.editOrder(res.order);
                            break;
                        case 'new':
                            this.startEmptyWorkTimeWithSpecificDate(res.order);
                            break;
                        case 'shareOrder':
                            // Auftrag aus Einsatzplanung wurde ausgewählt.
                            this.sharedOrder(res.order);
                            break;
                        case 'shareEmpOrder':
                            // Auftrag aus Auslastungsplanung wurde ausgewählt.
                            this.sharedEmpOrder(res.order);
                            break;
                        default:
                            if (!this.data$.getValue().auftrag) {
                                await this.loadSingleOrderData(res.order.rowID);
                            }
                    }
                },
            });
        }

        setTimeout(() => {
            this.shareDataService.currentScanData.pipe(first()).subscribe({
                next: async (res: string) => {
                    if (!res) {
                        return;
                    }
                    const data = this.data$.getValue();

                    data.scanData = res;
                    if (data.scanData[0] === 'A') {
                        data.scanData = data.scanData.slice(1);
                        const foundScannedOrder = this.IndexedDbData.orders.find((el: any) => el.objectNumber.trim() === data.scanData);
                        if (foundScannedOrder) {
                            data.auftrag = Object.assign({}, foundScannedOrder);
                            // await this.setObjectDescription(data, data.auftrag.objectID);
                            this.dataService.setCurrentTimeEntry(data).then(() => {
                                this.data$.next(data);
                                this.messageBoxService.showSuccessByLanguageKey('MAIN_APP_TIME.SCAN_SUCCESS.text', '');
                            });
                        } else {
                            this.messageBoxService.showErrorByLanguageKey('MAIN_APP_DIALOG.ERROR_DIALOG.BARCODE_ERROR.ORDER_NOT_FOUND.text', '');
                        }
                    } else if (data.scanData[0] === 'K') {
                        data.scanData = data.scanData.slice(1);
                        const foundScannedCostCenter = this.IndexedDbData.costs.find((el: any) => el.costCenterID === data.scanData);
                        if (foundScannedCostCenter) {
                            data.kostenStelle = Object.assign({}, foundScannedCostCenter!);

                            this.dataService.setCurrentTimeEntry(data).then(() => {
                                this.data$.next(data);
                                this.messageBoxService.showSuccessByLanguageKey('MAIN_APP_TIME.SCAN_SUCCESS.text', '');
                            });
                        } else {
                            this.messageBoxService.showSuccessByLanguageKey('MAIN_APP_TIME.ORDER_NOT_FOUND.text', '');
                        }
                    } else {
                        this.messageBoxService.showErrorByLanguageKey('MAIN_APP_DIALOG.ERROR_DIALOG.BARCODE_ERROR.WRONG_START.text', '');
                    }
                },
            });
        }, 200);
    }

    public async openAuftragDialog(): Promise<void> {
        const ORDER_DIALOG_WIDTH = '100vw';

        const data = {
            orders: this.IndexedDbData.orders, userPermissions: this.userPermissions, selectedOrder: this.data$.getValue().auftrag, costs: this.IndexedDbData.costs
        };
        if (!data.selectedOrder) {
            data.selectedOrder = EmptyOrderData().auftrag;
        }
        if (this.userPermissions) {
            const dialogRef = this.dialogService.openDialog(AuftragDialogComponent, data, false, ORDER_DIALOG_WIDTH, true);

            dialogRef.afterClosed().subscribe({
                next: async (result) => {
                    if (await this.dataService.isDayClosed()) {
                        return this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.DAY_CLOSED_ERROR.text', '');
                    }

                    if (!result || !result.value || !result.value.objectNumber) {
                        return;
                    }
                    await this.setOrderAndCostCenter(this.data$.getValue(), result.value);
                },
            });
        } else {
            this.messageBoxService.showSuccessByLanguageKey('MAIN_APP_TIME.NO_USER_RIGHTS_ERROR.text', '');
        }
    }

    public async saveAndResetTime(start: string, end: string): Promise<void> {
        // Wenn auf dem heutigen Tag eine Zeit erfasst ist, und der Tag bereits abgeschlossen ist,
        // dann soll blockiert werden
        const readyForExport = await this.checkReadyForExport(start);
        if (await this.dataService.isDayClosed()) {
            return this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.DAY_CLOSED_ERROR.text', '');
        }

        const data = this.data$.getValue();
        const user = this.userAuthService.getCurrentAuthenticatedUser();

        if (!data.kostenStelle) {
            return this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.NO_COST_CENTER_ERROR.text', '');
        }

        if (!data.auftrag) {
            return this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.NO_ORDER_ERROR.text', '');
        }
        if (!user) {
            return;
        }
        const baseDate = this.dataService.getChosenDate();
        const startTime = new Date(start || '');
        const endTime = new Date(end || '');

        startTime.setFullYear(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
        startTime.setSeconds(0, 0);
        endTime.setFullYear(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
        endTime.setSeconds(0, 0);

        // og end time and start time is only to set when its not 00:00 - 00:00
        const hasTimesToSet = startTime.getHours() > 0 && startTime.getMinutes() > 0 || startTime.getHours() > 0 && startTime.getHours() > 0;

        const dataToSave: TimeControlEntry = {
            rowID: uuidv4(),
            mandantID: user.mandatorID,
            employeeID: user.employeeID,
            objectID: data.auftrag.objectID ? data.auftrag.objectID : null,
            objectNumber: data.auftrag.objectNumber,
            description: data.kostenStelle.text ? data.kostenStelle.text : data.kostenStelle.description,
            costCenterID: data.kostenStelle.costCenterID,
            expenseID: data.spesen.expenseID,
            entryDate: moment(this.dataService.getChosenDate()).format(),
            startTime: null,
            endTime: null,
            timeAmount: null,
            expenseAmount: data.spesen.menge,
            expenseRate: data.spesen.betrag,
            note: data.notizen,
            workplace: data.kostenStelle.arbeitsOrt,
            readyForExport,
            exported: false,
            originalStartTime: hasTimesToSet ? startTime.toString() : null,
            originalEndTime: hasTimesToSet ? endTime.toString() : null,
            originalTimeAmount: data.total > 0 ? data.total : this.calculateTimeAmount(start, end),
            controlled: false,
            differencesExists: false,
            deleted: false,
        };

        if (startTime && endTime) {
            if (this.dataService.isToday && await this.dataService.hasTimeConflict(dataToSave.entryDate, startTime, endTime, data.editTimeEntryRowID)) {
                this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.TIME_CONFLICT.text', '');
                return;
            }
        }

        if (dataToSave.originalStartTime === '' && dataToSave.originalEndTime === '' && !dataToSave.originalTimeAmount) {
            return this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.NO_TIME_ENTERED_ERROR.text', '');
        } else {
            const prevChosenDate = this.dataService.getChosenDate();
            if (!this.userPermissions.allowTimerecordingInPast) {
                if (this.dataService.isInThePast(startTime)) {
                    return this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.ENTRY_IN_PAST_ERROR.text', '');
                } else {
                    await this.addTimeControlEntry(dataToSave);
                }
            } else {
                await this.addTimeControlEntry(dataToSave);
            }
            if (this.sharedMessage == 'new') {
                this.dataService.setChosenDate(prevChosenDate);
                this.router.navigateByUrl('timeapp/workTimes');
            }
        }
    }

    public async loadTimeControlEntries(): Promise<void> {
        const data = await this.localStorage.get('currentTimeEntry', 'SwissTimeEntry', false, EmptyOrderData());
        if (!data.kostenStelle) {
            data.kostenStelle = await this.getDefaultKostenStelleValues();
        }
        if (data.auftrag.objectNumber) {
            this.toggleIcon = true;
        }
        this.data$.next(data);
    }

    async loadSingleOrderData(rowID: string): Promise<void> {
        if (this.IndexedDbData.timeEntry.length > 0 && rowID) {
            const data = this.data$.getValue();

            const timeControlEntry = this.IndexedDbData.timeEntry.find((item) => item.rowID === rowID);
            if (!timeControlEntry) {
                throw Error(`Time control entry does not exist: ${rowID}`);
            }
            const order = this.IndexedDbData.orders.find((el) => el.objectID === timeControlEntry.objectID);
            const spesen = this.IndexedDbData.spesen.find((el) => el.expenseID === timeControlEntry.expenseID);
            const costCenter = this.IndexedDbData.costs.find((el) => el.costCenterID === timeControlEntry.costCenterID);

            data.auftrag.objectID = timeControlEntry.objectID!;
            data.auftrag.objectNumber = timeControlEntry.objectNumber;
            data.auftrag.lastname = order?.lastname!;
            data.auftrag.note4 = order?.note4!;

            data.kostenStelle = Object.assign({}, costCenter);
            if (!data.kostenStelle) {
                data.kostenStelle = EmptyCostCenter();
            }

            data.spesen.description = spesen?.description!;
            data.spesen.menge = timeControlEntry.expenseAmount;
            data.spesen.betrag = timeControlEntry.expenseRate;
            data.spesen.expenseID = timeControlEntry.expenseID;
            data.start = '';
            data.end = '';
            data.notizen = timeControlEntry.note;
            data.auftrag.street1 = order?.street1!;
            data.auftrag.city = order?.city!;
            data.auftrag.phone = order?.phone!;
            data.auftrag.phone03 = order?.phone03!;

            this.data$.next(data);
        }
    }

    public async cameraGuard(camera?): Promise<boolean> {
        if (!(await this.isRealOrder(this.data$.getValue().auftrag))) {
            if (this.data$.getValue().auftrag.objectNumber) {
                this.messageBoxService.showErrorByLanguageKey('MAIN_APP_DIALOG.ERROR_DIALOG.TIME_APP_ERROR.PSEUDO_ORDER_UPLOAD.text', '');
            } else {
                this.messageBoxService.showErrorByLanguageKey('MAIN_APP_DIALOG.ERROR_DIALOG.TIME_APP_ERROR.ORDER_NOT_FOUND.text', '');
            }
            return false;
        }
        if (camera) {
            camera.click();
        }
        return true;
    }

    protected async openKostenstelleDialog(): Promise<void> {
        if (await this.dataService.isDayClosed()) {
            return this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.DAY_CLOSED_ERROR.text', '');
        }

        const data = {
            costs: this.IndexedDbData.costs,
            userPermissions: this.userPermissions,
            selectedCosts: this.data$.getValue().kostenStelle,
            desc: this.data$.getValue().auftrag.objectSummary,
            recommendedWorkplace: this.data$.getValue().auftrag.recomendedWorkPlace
        };

        if (!(this.data$.getValue().auftrag?.recomendedWorkPlace?.trim() == '') && data.selectedCosts?.obligatoryWorkplace && data.selectedCosts?.arbeitsOrt?.trim() == '') {
            data.selectedCosts.arbeitsOrt = data.recommendedWorkplace;
        }

        const dialogRef = this.dialogService.openDialog(KostenstelleDialogComponent, data, true, 'auto', true);
        dialogRef.afterClosed().subscribe({
            next: async (result) => {
                if (!result) {
                    return;
                }
                const orderData = this.data$.getValue();
                orderData.kostenStelle = EmptyCostCenter();
                orderData.kostenStelle.arbeitsOrt = result.value.arbeitsOrt;
                orderData.kostenStelle.costCenterID = result.value.costCenterID;
                orderData.kostenStelle.description = result.value.description;
                orderData.kostenStelle.kostenStelle = result.value.kostenStelle;
                orderData.kostenStelle.text = result.value.text;
                orderData.kostenStelle.obligatoryWorkplace = result.value.obligatoryWorkplace;

                await this.dataService.setCurrentTimeEntry(orderData);
                this.data$.next(orderData);
            }
        });
    }

    protected async openStartTimeDialog(): Promise<void> {
        if (await this.dataService.isDayClosed()) {
            this.messageBoxService.showSuccessByLanguageKey('MAIN_APP_TIME.DAY_CLOSED_ERROR.text', '');
        }

        this.messageBoxService.showInputDialog<string>('MAIN_APP_DIALOG.INPUT_DIALOG.START_TIME.title', 'MAIN_APP_DIALOG.INPUT_DIALOG.START_TIME.title', this.data$.getValue().start, 'MAIN_APP_DIALOG.INPUT_DIALOG.save', 'MAIN_APP_DIALOG.INPUT_DIALOG.cancel', 'time', true).then(async result => {
            if (!result || result.value === '' || !result.doSave) {
                return;
            }
            const data = this.data$.getValue();
            data.total = 0;

            let chosenDate = await this.dataService.getChosenDateAsPromise();
            chosenDate = this._datumFormatPipe.addTimeToDate(chosenDate, result.value);
            data.start = moment(chosenDate).format();

            if (data.end) {
                data.total = this.calculateTimeAmount(data.start, data.end);
            }

            this.toggleIcon = true;

            await this.dataService.setCurrentTimeEntry(data);
            this.data$.next(data);
        });
    }

    protected async openEndTimeDialog(): Promise<void> {
        if (await this.dataService.isDayClosed()) {
            this.messageBoxService.showSuccessByLanguageKey('MAIN_APP_TIME.DAY_CLOSED_ERROR.text', '');
            return;
        }

        const result = await this.messageBoxService.showInputDialog<string>('MAIN_APP_DIALOG.INPUT_DIALOG.END_TIME.title', 'MAIN_APP_DIALOG.INPUT_DIALOG.END_TIME.title', this.data$.getValue().end, 'MAIN_APP_DIALOG.INPUT_DIALOG.save', 'MAIN_APP_DIALOG.INPUT_DIALOG.cancel', 'time', true);
        if (!result?.doSave) {
            return;
        }
        const data = this.data$.getValue();
        if (data.start === '') {
            this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.NO_START_TIME_ERROR.text', '');
            return;
        }

        const endTime = this._datumFormatPipe.addTimeToDate(this.dataService.getChosenDate(), result.value);
        if (endTime < new Date(data.start)) {
            data.end = '';
            data.total = 0;
            this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.END_GREATER_START_ERROR.text', '');
            return;
        }

        data.end = moment(endTime).format();
        data.total = this.calculateTimeAmount(data.start, data.end);
        await this.dataService.setCurrentTimeEntry(data);
        this.data$.next(data);
    }

    protected async openTimeAmountDialog(): Promise<void> {
        if (await this.dataService.isDayClosed()) {
            this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.DAY_CLOSED_ERROR.text', '');
            return;
        }
        this.messageBoxService.showInputDialog<any>('MAIN_APP_DIALOG.INPUT_DIALOG.TOTAL_TIME.title', 'MAIN_APP_DIALOG.INPUT_DIALOG.TOTAL_TIME.title', this.data$.getValue().total || null, 'MAIN_APP_DIALOG.INPUT_DIALOG.save', 'MAIN_APP_DIALOG.INPUT_DIALOG.cancel', 'number', true, false, {
            max: 24,
            min: 0
        }).then(async result => {
            if (result.doSave) {
                const data = this.data$.getValue();
                data.start = '';
                data.end = '';
                data.total = parseFloat(result.value);
                await this.dataService.setCurrentTimeEntry(data);
                this.data$.next(data);
            }
        });
    }

    protected async openSpesenDialog(): Promise<void> {
        if (await this.dataService.isDayClosed()) {
            this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.DAY_CLOSED_ERROR.text', '');
            return;
        }
        const data = {
            spesen: this.IndexedDbData.spesen, selectedSpesen: this.data$.getValue().spesen,
        };
        const dialogRef = this.dialogService.openDialog(SpesenDialogComponent, data, true, 'auto', true);

        dialogRef.afterClosed().subscribe({
            next: async (result) => {
                if (!result) {
                    return;
                }
                const orderData = this.data$.getValue();
                orderData.spesen.amountOne = result.value.amountOne;
                orderData.spesen.expenseID = result.value.expenseID;
                orderData.spesen.rate = result.value.rate;
                orderData.spesen.description = result.value.description;
                orderData.spesen.menge = result.value.menge;
                orderData.spesen.betrag = result.value.betrag;

                await this.dataService.setCurrentTimeEntry(orderData);
                this.data$.next(orderData);
            },
        });
    }

    protected async openNotizenDialog(): Promise<void> {
        if (await this.dataService.isDayClosed()) {
            this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.DAY_CLOSED_ERROR.text', '');
            return;
        }

        const result = await this.messageBoxService.showInputDialog<string>('', 'MAIN_APP_DIALOG.INPUT_DIALOG.TIME_APP_NOTES.label', this.data$.getValue().notizen, 'MAIN_APP_DIALOG.INPUT_DIALOG.save', 'MAIN_APP_DIALOG.INPUT_DIALOG.cancel', 'text', true);
        if (!result?.doSave) {
            return;
        }
        const data = this.data$.getValue();
        data.notizen = result.value;
        await this.dataService.setCurrentTimeEntry(data);
        this.data$.next(data);
    }

    protected resetDataDialog(): void {
        this.messageBoxService.askYesNoQuestion('MAIN_APP_DIALOG.RESET_DAY.text', '', [], [], false, 'MAIN_APP_DIALOG.confirm', 'MAIN_APP_DIALOG.cancel', true).then(async result => {
            if (result) {
                await this.reset();
            }
        });
    }

    protected async startEndTime(): Promise<void> {
        if (await this.dataService.isDayClosed()) {
            this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.DAY_CLOSED_ERROR.text', '');
            return;
        }
        if (this.userPermissions.noDataEditOnHandy === 1 && this.data$.getValue().isEditMode) {
            this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.MISSING_RIGHTS_ERROR.text', '');
            return;
        }
        //
        let start = moment().format();
        let end = moment().format();
        if (moment(start).seconds() >= 30) {
            start = moment(start).add(1, 'minutes').seconds(0).format();
        }

        if (moment(end).seconds() >= 30) {
            end = moment(end).add(1, 'minutes').seconds(0).format();
        }
        const data = this.data$.getValue();
        if (!data.start) {
            data.start = start;
            this.toggleIcon = true;
            await this.dataService.setCurrentTimeEntry(data);
            this.data$.next(data);
        } else {
            if (moment(end).format('HH:mm') < moment(data.start).format('HH:mm')) {
                data.end = '';
                this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.END_GREATER_START_ERROR.text', '');
            } else {
                this.toggleIcon = false;
                data.end = end;
                await this.dataService.setCurrentTimeEntry(data);
                this.data$.next(data);
                await this.saveTimeEntry();
            }
        }
    }

    protected async saveTimeEntry(): Promise<void> {
        const data = this.data$.getValue();

        if (this.userPermissions.noDataEditOnHandy === 1 && data.isEditMode) {
            return this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.MISSING_RIGHTS_ERROR.text', '');
        }
        if (data.auftrag?.objectNumber == '') {
            return this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.NO_ORDER_ERROR.text', '');
        }
        if (data.kostenStelle?.costCenterID == 0 && !data.kostenStelle?.description) {
            return this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.NO_COST_CENTER_ERROR.text', '');
        } else {
            // Prüfung, ob eine Ortschaft bei einer Kostenstelle mit pflichtigem Ort hinterlegt ist
            if (data.kostenStelle && !data.kostenStelle.arbeitsOrt) {
                const kostenStelle = this.IndexedDbData.costs.find((x) => x.costCenterID === data.kostenStelle?.costCenterID);
                if (kostenStelle && kostenStelle.obligatoryWorkplace) {
                    return this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.NO_WORK_PLACE_ERROR.text', '');
                }
            }
        }
        await this.saveAndResetTime(data.start, data.end);
    }

    protected async dataSync(): Promise<void> {
        if (!this.helperService.isWebServiceAvailable()) {
            return this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.NO_WEBSERVER_CONNECTION_ERROR.text', '');
        }
        if (this.svcVinavonUpdate.updateFound.getValue()) {
            await this.svcVinavonUpdate.askUserToDoUpdate();
            await this.syncDataPopUp();
        } else {
            await this.syncDataPopUp();
        }
    }

    protected async openCamera(event: any): Promise<void> {
        return this.captureImageService.capturedImage(event, this.data$.getValue().auftrag);
    }

    protected toggleLieferArchitect(): void {
        this.toggleBtn = !this.toggleBtn;
    }

    protected async navigateToSupplyManagement(): Promise<void> {
        if (!this.data$.getValue().auftrag.objectNumber) {
            return this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.ORDER_FOR_MATERIAL_REQUIRED_ERROR.text', '');
        }
        // TODO ask Emanuel if we really wanna use a string entered by the user as ID in general for PseudoOrders
        // is there another way to do that even in this case?
        await this.router.navigate(['supplies', this.data$.getValue().auftrag.objectID || 'pseudo-order', this.data$.getValue().auftrag.objectNumber], {relativeTo: this.route});
    }

    protected checkUserPermission(permission: string): boolean {
        if (permission == 'noDataEditOnHandy') {
            return !(this.userPermissions && this.userPermissions.noDataEditOnHandy === 0);
        } else if (permission === 'allowSaveHoursWithoutTime') {
            return this.userPermissions && this.userPermissions.allowSaveHoursWithoutTime;
        }
        return true;
    }

    protected async openCameraSafari(event: any): Promise<void> {
        const res = await this.cameraGuard();
        if (res) {
            return this.captureImageService.capturedImage(event, this.data$.getValue().auftrag);
        }
    }

    protected async navigateToGallery(): Promise<void> {
        if (!this.data$.getValue().auftrag.objectNumber || !this.data$.getValue().auftrag.objectID) {
            if (this.data$.getValue().auftrag.objectNumber) {
                return this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.IMG_ADDED_TO_PSEUDO_ORDER_ERROR.text', '');
            }
            return this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.IMG_ADDED_TO_PSEUDO_ORDER_ERROR.text', '');
        }

        await this.router.navigate(['gallery', this.data$.getValue().auftrag.objectID], {relativeTo: this.route});
    }

    protected async navigateToDocuments(): Promise<void> {
        if (!this.data$.getValue().auftrag.objectNumber || !this.data$.getValue().auftrag.objectID) {
            if (this.data$.getValue().auftrag.objectNumber) {
                return this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.DOC_ADDED_TO_PSEUDO_ORDER_ERROR.text', '');
            }
            return this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.DOC_ADDED_TO_PSEUDO_ORDER_ERROR.text', '');
        }

        await this.router.navigate(['documents', this.data$.getValue().auftrag.objectID], {relativeTo: this.route});
    }

    private async reset(order = EmptyOrder()): Promise<void> {
        this.toggleIcon = false;
        this.startEmptyWorkTime = false;
        await this.localStorage.delete('currentTimeEntry', 'SwissTimeEntry');
        const data = EmptyOrderData();
        data.kostenStelle = await this.getDefaultKostenStelleValues();
        await this.setOrderAndCostCenter(data, order);
    }

    private calculateTimeAmount(start: string, end: string): number {
        // Wenn Datum und Zeit (Stunden/Minuten) übereinstimmen => 0.00 zurückgeben.
        if (start.substring(0, 16) === end.substring(0, 16)) {
            return 0.0;
        }
        const hours = moment.duration(moment(start, 'YYYY/MM/DD hh:mm').diff(moment(end, 'YYYY/MM/DD hh:mm'))).asMinutes();
        return this.timeStringToFloat(Math.abs(hours));
    }

    private timeStringToFloat(num: any): number {
        let hours = num / 60;
        hours = +hours.toFixed(2);
        return hours;
    }

    private async getDefaultKostenStelleValues(): Promise<CostCenter> {
        if (!(this.IndexedDbData.costs && this.IndexedDbData.costs.length > 0)) {
            return EmptyCostCenter();
        }
        const user = this.userAuthService.getCurrentAuthenticatedUser();
        if (!user) {
            throw Error();
        }
        const defaultCostCenter = await this.localStorage.get('DefaultCostCenterID', 'SwissWebConfigDB', false, user.costCenter);
        const co = this.IndexedDbData.costs.find((el) => el.costCenterID === defaultCostCenter);

        return co || EmptyCostCenter();
    }

    private async copyOrder(orderList: OrderListElement): Promise<void> {
        const order = this.getOrder(orderList.objectID, orderList.objectNumber);
        const costCenter = this.getCostCenter(orderList.costCenterID);
        if (costCenter) {
            costCenter.text = orderList.description;
            costCenter.arbeitsOrt = orderList.workplace;
        }

        await this.setOrderAndCostCenter(EmptyOrderData(), order, costCenter);
    }

    private async editOrder(orderList: OrderListElement): Promise<void> {
        const data = EmptyOrderData();
        const order = this.getOrder(orderList.objectID, orderList.objectNumber);
        const costCenter = this.getCostCenter(orderList.costCenterID);
        const foundSpesen = this.IndexedDbData.spesen.find((el) => el.expenseID === orderList.expenseID);
        this.toggleIcon = true;

        data.isEditMode = true;
        data.editTimeEntryDate = orderList.entryDate;

        if (orderList.originalStart !== orderList.originalEnd || orderList.originalTimeAmount === 0) {
            let chosenDate = await this.dataService.getChosenDateAsPromise();
            chosenDate = this._datumFormatPipe.addTimeToDate(chosenDate, orderList.originalStart);
            data.start = moment(chosenDate).format();
            chosenDate = this._datumFormatPipe.addTimeToDate(chosenDate, orderList.originalEnd);
            data.end = moment(chosenDate).format();
        }
        data.total = orderList.originalTimeAmount!;
        data.notizen = orderList.note;
        if (foundSpesen) {
            data.spesen.expenseID = foundSpesen.expenseID;
            data.spesen.description = foundSpesen.description;
        }
        data.spesen.menge = orderList.expenseAmount;
        data.spesen.betrag = orderList.expenseRate;
        data.editTimeEntryRowID = orderList.rowID;
        if (costCenter) {
            costCenter.text = orderList.description;
            costCenter.arbeitsOrt = orderList.workplace;
        }

        await this.setOrderAndCostCenter(data, order, costCenter);
    }

    private async setOrderAndCostCenter(orderData: OrderData, order: Orders, selectedCostCenter?: CostCenter): Promise<void> {
        const data = orderData;
        const prevOrder = data.auftrag;
        data.auftrag = Object.assign({}, order);

        const costCenter = await this.dataService.getCostCenterByAuftrag(data.auftrag, (selectedCostCenter || data.kostenStelle || EmptyCostCenter()));
        if (costCenter && costCenter.costCenterID !== 0) {
            data.kostenStelle = costCenter;
            if (data.kostenStelle.obligatoryWorkplace && ((!data.kostenStelle.arbeitsOrt?.trim() || !prevOrder || data.kostenStelle.arbeitsOrt.trim() === prevOrder.recomendedWorkPlace.trim())))  {
                data.kostenStelle.arbeitsOrt = data.auftrag.recomendedWorkPlace.trim();
            }
        }

        this.toggleIcon = !(data.start === '' || data.start === undefined);

        await this.dataService.setCurrentTimeEntry(data);
        this.data$.next(data);
    }

    private async startEmptyWorkTimeWithSpecificDate(orderList: OrderListElement): Promise<void> {
        const data = EmptyOrderData();
        const order = this.getOrder(orderList.objectID, orderList.objectNumber);
        const costCenter = await this.getDefaultKostenStelleValues();

        await this.setOrderAndCostCenter(data, order, costCenter);
    }

    private async sharedOrder(order: EmpWorkPlan): Promise<void> {
        if (this.IndexedDbData === undefined) {
            this.IndexedDbData = await this.dataService.getAllDataFromWebServices();
        }
        let foundOrder = this.IndexedDbData.orders.find((el) => el.objectID === order.objectID);
        if (!foundOrder) {
            // reload orders from webservice into IndexDB.orders if no order could be found
            const orders = await this.dataService.reloadOrdersFromWebservice();
            if (orders) {
                this.IndexedDbData.orders = orders;
                foundOrder = this.IndexedDbData.orders.find((el) => el.objectID === order.objectID);
                if (foundOrder) {
                    return await this.loadSharedOrder(foundOrder);
                }
            }
            return this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.ORDER_NOT_FOUND.text', '');
        } else {
            await this.loadSharedOrder(foundOrder);
        }
    }

    private getOrder(orderID: string, orderName?: string): Orders {
        let order = this.IndexedDbData.orders.find((el) => el.objectID === orderID);
        if (order) {
            return order;
        }
        order = EmptyOrder();
        order.objectNumber = orderName || '';
        return order;
    }

    private getCostCenter(costCenterID: number): CostCenter | undefined {
        return this.IndexedDbData.costs.find((el) => el.costCenterID === costCenterID);
    }

    private async sharedEmpOrder(ewp: EmpWeekPlan): Promise<void> {
        if (this.IndexedDbData === undefined) {
            this.IndexedDbData = await this.dataService.getAllDataFromWebServices();
        }
        const foundOrder = this.IndexedDbData.orders.find((el) => el.objectID === ewp.objectID);
        if (!foundOrder) {
            return this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.ORDER_NOT_FOUND.text', '');
        }
        this.data$.next(EmptyOrderData());
        await this.setOrderAndCostCenter(this.data$.getValue(), foundOrder, await this.getDefaultKostenStelleValues());
    }

    private async getAllOrdersWebCall(): Promise<void> {
        const res = await this.dataService.getAllOrders();
        if (!res) {
            return;
        }

        this.IndexedDbData.orders = res;
        await this.localStorage.delete('orders', 'SwissTimeEntry', true);
        await this.localStorage.add('orders', this.IndexedDbData.orders, 'SwissTimeEntry', false, true);
    }

    private async syncDataPopUp(): Promise<void> {
        const syncData = await this.messageBoxService.askYesNoQuestion('HOME.Data Sync', '', [], [], false, 'MAIN_APP_DIALOG.confirm', 'MAIN_APP_DIALOG.cancel', true);
        if (!this.helperService.isWebServiceAvailable()) {
            return this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.NO_WEBSERVER_CONNECTION_ERROR.text', '');
        }
        // refresh localstorage 'useranwendung' to then check it
        await this.userAuthService.getUserLicenses();
        const hasMobileLicense = await this.userAuthService.hasLicences(UserLicenses.CMobileTime);
        if (!hasMobileLicense) {
            await this.messageBoxService.showErrorOK('MAIN_APP_TIME.REVOKED_LICENCES.text', 'MAIN_APP_TIME.REVOKED_LICENCES.title', ['' + this.userAuthService.getCurrentAuthenticatedUser()?.employeeID]);
        }

        if (syncData) {
            try {
                this.helperService.lockApp();
                let syncErrorExists = await this.dataService.startSyncData(undefined);
                await this.reloadData();
                const orders = await this.dataService.reloadOrdersFromWebservice();
                if (orders) {
                    this.IndexedDbData.orders = orders;
                }
                await this.getAllEmployeesWebCall();
                syncErrorExists = syncErrorExists || await this.dataService.syncImages();

                if (!syncErrorExists) {
                    this.messageBoxService.showSuccessByLanguageKey('MAIN_APP_TIME.SYNC_SUCCESSFUL.text', '');
                } else {
                    this.messageBoxService.showSuccessByLanguageKey('MAIN_APP_TIME.SYNC_ERROR.text', '');
                }
            } catch (ex) {
                console.error(ex);
                this.messageBoxService.showSuccessByLanguageKey('MAIN_APP_TIME.SYNC_ERROR.text', '');
            } finally {
                if (!hasMobileLicense) {
                    await this.userAuthService.logoutUser();
                }
                this.helperService.unlockApp();
            }
        }
    }

    private async getAllEmployeesWebCall(): Promise<void> {
        // Die Mitarbeiter können nur abgeholt werden, wenn eine EmployeeID vorhanden ist
        if (this.userAuthService.getCurrentAuthenticatedUser()) {
            const res = await this.dataService.getAllEmployee();
            if (!res) {
                return;
            }
            this.IndexedDbData.allEmployees = res;
            await this.updateUserPermissions();
        }
    }

    private async isRealOrder(order): Promise<Orders | undefined> {
        const orders = await this.localStorage.get('orders', 'SwissTimeEntry', true, []);
        return orders.find((el: any) => el.objectID == order.objectID);
    }

    private async updateUserPermissions(): Promise<boolean> {
        return new Promise((resolve) => {
            if (!this.userPermissions) {
                this.findUserPermissions().then(() => {
                    this.getAllEmployeesWebCall();
                    resolve(true);
                });
            } else {
                this.findUserPermissions().then(() => {
                    resolve(true);
                });
            }
        });
    }

    private async findUserPermissions(): Promise<boolean> {
        return new Promise((resolve) => {
            const anwender = this.userAuthService.getCurrentAuthenticatedUser();
            if (anwender === undefined) {
                this.userAuthService.getUserData().then(() => {
                    this.loadUserPermissions().then(() => resolve(true));
                }).catch(() => resolve(false));
            } else {
                this.loadUserPermissions().then(() => resolve(true)).catch(() => resolve(false));
            }
        });
    }

    private async addTimeControlEntry(timeControlEntry: TimeControlEntry): Promise<void> {
        const data = this.data$.getValue();

        const x = await this.dataService.saveTimeControlEntryToIndexedDB(timeControlEntry, data.editTimeEntryRowID, data.editTimeEntryDate);
        if (x) {
            if (data.total === 0) {
                this.messageBoxService.showSuccessByLanguageKey('MAIN_APP_TIME.TIME_SAVED_FROM_TO.text', '', [moment(data.start).format('HH:mm'), moment(data.end).format('HH:mm')]);
            } else {
                this.messageBoxService.showSuccessByLanguageKey('MAIN_APP_TIME.TIME_SAVED.text', '');
            }
            this.toggleIcon = false;
            const foundOrder = this.IndexedDbData.orders.find((el) => el.objectID === timeControlEntry.objectID);
            await this.reset(foundOrder);
            this.dataService.resetChosenDate();
        }
    }

    private async starOrderTime(orderList: OrderListElement): Promise<void> {
        const data = EmptyOrderData();
        const order = this.getOrder(orderList.objectID, orderList.objectNumber);
        const costCenter = this.getCostCenter(orderList.costCenterID);
        if (costCenter) {
            costCenter.text = orderList.description;
            costCenter.arbeitsOrt = orderList.workplace;
        }
        data.start = moment().format();

        await this.setOrderAndCostCenter(data, order, costCenter);
    }

    private loadUserPermissions(): Promise<boolean> {
        return new Promise((resolve) => {
            this.localStorage.get('allEmployees', 'SwissTimeEntry').then(res => {
                this.userPermissions = res.find((el: any) => el.employeeID === this.userAuthService.getCurrentAuthenticatedUser()?.employeeID)!;
                resolve(!!this.userPermissions);
            });
        });
    }

    private async checkReadyForExport(start: string): Promise<boolean> {
        const today = new Date();
        let pruefDatum: string;
        if (start !== '') {
            pruefDatum = start;
        } else {
            pruefDatum = moment(this.dataService.getChosenDate()).format();
        }

        if (pruefDatum !== '' && pruefDatum.substring(0, 11) === moment(today).format().substring(0, 11)) {
            // Es ist der heutige Tag, ist dieser auch abgeschlossen?
            if (await this.dataService.isDayClosed(this._datumFormatPipe.transformOnlyDate(today))) {
                return true;
            }
        }
        return false;
    }

    private async dataServiceDataLoaded(): Promise<void> {
        await this.captureImageService.cleanImages();
        await this.getDataFromLocalStorage();
        this.afterInit();
    }

    private async reloadData(): Promise<void> {
        const user = this.userAuthService.getCurrentAuthenticatedUser();
        if (!user) {
            return;
        }
        if (user.orderSourceVinavonTime === 1) {
            // Aufträge von Einsatzplan laden
            this.dataService.getPlannedEmployeeWorks().then((res) => {
                if (!res) {
                    return;
                }
                this.IndexedDbData.plannedEmployeeWorks = res;
            });
        } else if (user.orderSourceVinavonTime === 2) {
            // Aufträge von Mitarbeiterplanung (Auslastungsplanung) laden
            this.dataService.getPlannedEmployeeWeeks().then((res) => {
                if (!res) {
                    return;
                }
                this.IndexedDbData.plannedEmployeeWeeks = res;
            });
        }
        this.dataService.getAllOrders().then((res) => {
            this.IndexedDbData.orders = res;
            this.localStorage.delete('orders', 'SwissTimeEntry', true);
            this.localStorage.add('orders', this.IndexedDbData.orders, 'SwissTimeEntry', false, true);
        });
    }

    private async loadSharedOrder(foundOrder: Orders): Promise<void> {
        this.data$.next(EmptyOrderData());
        await this.setOrderAndCostCenter(this.data$.getValue(), foundOrder, await this.getDefaultKostenStelleValues());
    }
}
